/**
 * i18nLoader — async-import per-lang JSON from same host with English
 * fallback and in-memory cache.
 *
 * Uses `new URL()` so Parcel statically analyses the path and emits
 * the JSON files as addressable static assets in the output bundle.
 */

const _cache = new Map()
const _loadedNamespaces = new Set()

// Supported languages and namespaces — Parcel resolves these at build time.
const LANGS = ['en', 'de', 'fr', 'es']
const NAMESPACES = ['common', 'auth', 'account', 'admin']

/**
 * Resolve the URL for a given lang/namespace bundle via new URL() so
 * Parcel includes the JSON as a static asset and assigns it a stable
 * content-hashed URL in the output.
 *
 * @param {string} lang
 * @param {string} namespace
 * @returns {string}
 */
function resolveI18nUrl (lang, namespace) {
  // new URL() with a relative string + import.meta.url lets Parcel detect
  // the asset at build time and rewrite the URL to its hashed output path.
  if (lang === 'en' && namespace === 'common') {
    return new URL('../i18n/en/common.json', import.meta.url).href
  }
  if (lang === 'en' && namespace === 'auth') {
    return new URL('../i18n/en/auth.json', import.meta.url).href
  }
  if (lang === 'en' && namespace === 'account') {
    return new URL('../i18n/en/account.json', import.meta.url).href
  }
  if (lang === 'en' && namespace === 'admin') {
    return new URL('../i18n/en/admin.json', import.meta.url).href
  }
  if (lang === 'de' && namespace === 'common') {
    return new URL('../i18n/de/common.json', import.meta.url).href
  }
  if (lang === 'de' && namespace === 'auth') {
    return new URL('../i18n/de/auth.json', import.meta.url).href
  }
  if (lang === 'de' && namespace === 'account') {
    return new URL('../i18n/de/account.json', import.meta.url).href
  }
  if (lang === 'de' && namespace === 'admin') {
    return new URL('../i18n/de/admin.json', import.meta.url).href
  }
  if (lang === 'fr' && namespace === 'common') {
    return new URL('../i18n/fr/common.json', import.meta.url).href
  }
  if (lang === 'fr' && namespace === 'auth') {
    return new URL('../i18n/fr/auth.json', import.meta.url).href
  }
  if (lang === 'fr' && namespace === 'account') {
    return new URL('../i18n/fr/account.json', import.meta.url).href
  }
  if (lang === 'fr' && namespace === 'admin') {
    return new URL('../i18n/fr/admin.json', import.meta.url).href
  }
  if (lang === 'es' && namespace === 'common') {
    return new URL('../i18n/es/common.json', import.meta.url).href
  }
  if (lang === 'es' && namespace === 'auth') {
    return new URL('../i18n/es/auth.json', import.meta.url).href
  }
  if (lang === 'es' && namespace === 'account') {
    return new URL('../i18n/es/account.json', import.meta.url).href
  }
  if (lang === 'es' && namespace === 'admin') {
    return new URL('../i18n/es/admin.json', import.meta.url).href
  }
  // Fallback: runtime-only URL, not statically bundled by Parcel.
  return `/i18n/${lang}/${namespace}.json`
}

/**
 * Load a translation namespace for the given language.
 * Falls back to 'en' if the requested language bundle is not found.
 *
 * @param {string} lang       - ISO language code (e.g. 'fr', 'ar')
 * @param {string} namespace  - bundle name (e.g. 'common', 'tickets')
 * @returns {Promise<object>}
 */
export const loadI18nNamespace = async (lang, namespace) => {
  const key = `${lang}/${namespace}`
  if (_cache.has(key)) return _cache.get(key)
  const url = resolveI18nUrl(lang, namespace)
  try {
    const res = await fetch(url, { credentials: 'same-origin' })
    if (!res.ok) {
      if (lang !== 'en') return loadI18nNamespace('en', namespace)
      return {}
    }
    const data = await res.json()
    _cache.set(key, data)
    _loadedNamespaces.add(namespace)
    return data
  } catch (e) {
    if (lang !== 'en') return loadI18nNamespace('en', namespace)
    return {}
  }
}

/**
 * Evict all non-English cache entries (called before re-loading in a new lang).
 */
export const dropNonEnglishCache = () => {
  for (const k of _cache.keys()) {
    if (!k.startsWith('en/')) _cache.delete(k)
  }
}

/**
 * Return the list of namespaces that have been loaded at least once.
 * @returns {string[]}
 */
export const getLoadedNamespaces = () => Array.from(_loadedNamespaces)

/**
 * Merge all loaded namespace bundles for a given language into a single object.
 * Falls back to the English bundle for any namespace not yet cached for lang.
 *
 * @param {string} lang
 * @returns {object}
 */
export const mergeAllNamespaces = (lang) => {
  const merged = {}
  for (const ns of _loadedNamespaces) {
    Object.assign(merged, _cache.get(`${lang}/${ns}`) || _cache.get(`en/${ns}`) || {})
  }
  return merged
}

export { LANGS, NAMESPACES }
